import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { ChevronDown, ChevronUp, X, Mic2, Play, Pause } from "lucide-react";
import VuMeter from "@/components/VuMeter";

const STORAGE_KEY = "drr_player_bar_dismissed";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Routes where the persistent player bar should NOT appear.
// - /chat is embedded inside the official mobile apps which already have their own player
// - /admin is the control panel, doesn't need to play music
// - /embed is the iframe widget — must render only the widget itself
const HIDDEN_ROUTES = ["/chat", "/admin", "/embed"];

/**
 * Persistent floating Live365 player bar pinned to the bottom of the viewport
 * on every route. Music keeps playing as listeners navigate between Home,
 * DJ profiles, etc. — because this iframe never unmounts.
 *
 * Mounted in App.js outside of <Routes>.
 */
export default function PersistentPlayerBar() {
  const location = useLocation();
  const onHiddenRoute = HIDDEN_ROUTES.includes(location.pathname);

  const [collapsed, setCollapsed] = useState(false);
  const [hidden, setHidden] = useState(() => {
    try {
      return sessionStorage.getItem(STORAGE_KEY) === "1";
    } catch (e) {
      return false;
    }
  });

  // Push the page content up so the bar doesn't cover the footer
  useEffect(() => {
    const isVisible = !hidden && !onHiddenRoute;
    const pad = !isVisible ? 0 : collapsed ? 40 : 110;
    document.body.style.paddingBottom = `${pad}px`;
    return () => {
      document.body.style.paddingBottom = "";
    };
  }, [collapsed, hidden, onHiddenRoute]);

  if (hidden || onHiddenRoute) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-40 border-t-2 border-red-600 bg-black/95 backdrop-blur-sm shadow-[0_-4px_24px_rgba(0,0,0,0.6)]"
      data-testid="persistent-player-bar"
    >
      {/* Header strip — always visible, toggles collapse */}
      <div className="flex items-center justify-between px-3 md:px-6 h-10 border-b border-zinc-800">
        <div className="flex items-center gap-3 min-w-0">
          <span className="on-air-dot shrink-0" aria-hidden />
          <span className="font-display text-sm md:text-base tracking-tight text-zinc-50 truncate">
            DIGITAL <span className="text-red-600">REVOLUTION</span> RADIO
          </span>
          <span className="hidden md:inline text-[10px] text-zinc-500 tracking-widest uppercase">
            //_keep_listening_while_you_browse
          </span>
        </div>
        <VuMeter width={54} height={26} bands={8} className="shrink-0 mx-2" />
        <FeaturedJingleChip />
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={() => setCollapsed((c) => !c)}
            className="text-zinc-300 hover:text-red-500 p-1 transition-colors"
            aria-label={collapsed ? "Expand player" : "Collapse player"}
            data-testid="player-bar-toggle"
          >
            {collapsed ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </button>
          <button
            onClick={() => {
              try {
                sessionStorage.setItem(STORAGE_KEY, "1");
              } catch (e) {
                /* silent */
              }
              setHidden(true);
            }}
            className="text-zinc-300 hover:text-red-500 p-1 transition-colors"
            aria-label="Close player bar (this session)"
            data-testid="player-bar-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Live365 mini-player — stays mounted across all route changes */}
      {!collapsed && (
        <div className="flex items-center justify-center px-2 py-1 bg-zinc-950">
          <iframe
            title="DRR Persistent Player"
            width="100%"
            height="60"
            frameBorder="0"
            src="https://live365.com/embeds/v1/player/a05268?s=sm&m=dark&autoplay=1&a=1"
            allow="autoplay; encrypted-media"
            data-testid="persistent-player-iframe"
            className="max-w-3xl block"
          />
        </div>
      )}
    </div>
  );
}

/**
 * Tiny "🎙️ //_station_id" chip in the persistent player header. Polls
 * /api/featured-jingle for whatever ident the admin has starred. Click
 * toggles play/pause. Auto-hides when nothing's featured so the chip
 * never appears as dead UI.
 *
 * Audio is loaded once from a base64 data URL — no network on play —
 * so it kicks in instantly without interfering with the Live365 stream
 * iframe (browsers happily mix the two outputs).
 */
function FeaturedJingleChip() {
  const [jingle, setJingle] = useState(null);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const fetchFeatured = async () => {
      try {
        const res = await axios.get(`${API}/featured-jingle`);
        if (cancelled) return;
        setJingle(res?.data?.ok ? res.data : null);
      } catch (e) {
        if (!cancelled) setJingle(null);
      }
    };
    fetchFeatured();
    // 5 min refresh — matches the Today's Vibe banner cadence so the chip
    // updates after the admin stars a new jingle without a hard refresh.
    const id = setInterval(fetchFeatured, 5 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  // Reset state if the featured jingle changes underneath the player.
  useEffect(() => {
    setPlaying(false);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  }, [jingle?.id]);

  const handleToggle = () => {
    const el = audioRef.current;
    if (!el || !jingle?.audio_url) return;
    if (playing) {
      el.pause();
    } else {
      el.currentTime = 0;
      el.play().catch(() => {
        /* user-gesture browsers may block — silent */
        setPlaying(false);
      });
    }
  };

  if (!jingle?.ok || !jingle.audio_url) return null;

  return (
    <button
      type="button"
      onClick={handleToggle}
      className={`hidden md:flex shrink-0 items-center gap-1.5 px-2 h-7 border text-[10px] tracking-[0.25em] uppercase font-display transition-colors ${
        playing
          ? "border-red-500 bg-red-600/15 text-red-400"
          : "border-zinc-700 text-zinc-300 hover:text-red-500 hover:border-red-600"
      }`}
      title={`Station ID: ${jingle.name}${jingle.voice_label ? " — " + jingle.voice_label : ""}`}
      aria-label={playing ? "Stop station ID" : "Play station ID"}
      data-testid="persistent-featured-jingle-chip"
    >
      {playing ? (
        <Pause className="w-3 h-3" strokeWidth={2.5} />
      ) : (
        <Mic2 className="w-3 h-3" strokeWidth={2.5} />
      )}
      <span>//_station_id</span>
      <audio
        ref={audioRef}
        src={jingle.audio_url}
        preload="auto"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
      />
    </button>
  );
}
